<template>
    <div class="sfc-student-loan-calc">
        <div class="container-fluid wave">
            <div class="sfc-container">
				<h1>Cost of Deferment Calculator</h1>
				<!--Schema Markup FAQs-->
				<script v-html="create_schema(FAQList)" type="application/ld+json"/>
				<p class="gray-text">
					This cost of deferment calculator analyzes the cost of suspending payments on your student loans for a period of time using a deferment or forbearance.
				</p>
				<div class="sfc-card main">
					<div id="res" v-if="resultDisplayed" style="padding:25px;width:100%" class="sfc-row results content block border-bottom">
						<h4>
							<div class="social-media-area-floated mb-4">
								<sfc-share-component :hero="`https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg`" title="Cost of Deferment Calculator" :url="`${this.$store.state.config.sfcHost}/calculators/cost-of-deferment-calculator`" ></sfc-share-component>
							</div>
						</h4>
						<div class="sfc-row" style="background-color: #125393;padding: 5px;border-radius:5px">
                                <h4 style="color:white">Your Results</h4>
						</div>
						<div class="sfc-row">
							<div class="sfc-col-12">
								<table class="list flex income-driven-results white-bg">
									<tbody>
										<tr class="table-header">
											<td></td>
											<td align="right">Before Deferment</td>
											<td align="right">After Deferment</td>
											<td align="right">Difference</td>
										</tr>
										<tr class="list-row">
											<td align="right" class="gray-text text-left">
												Total Student Loan Payments
											</td>
											<td class="large" align="right">${{formatNumber(output.totalPaymentBeforeDeferrment)}}</td>
											<td class="large" align="right">${{formatNumber(output.totalPaymentAfterDeferrment)}}</td>
											<td align="right" class="larger text-navy text-primary">${{formatNumber(output.totalDifference)}}</td>
										</tr>
										<tr class="list-row">
											<td align="right" class="gray-text text-left">
												Monthly Payment
											</td>
											<td class="large" align="right">${{formatNumber(output.monthlyPaymentBeforeDeferrment)}}</td>
											<td class="large" align="right">${{formatNumber(output.monthlyPaymentAfterDeferrment)}}</td>
											<td align="right" class="larger text-navy text-negative">${{formatNumber(output.monthlyDifference)}}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>


						<!-- <div class="sfc-row" style="background-color: #125393;padding: 5px;border-radius:5px">
							<h4 style="color:white">
								Your Results
							</h4>
						</div>
						<div class="sfc-row">
							<div class="sfc-col-12">
								<table class="list content text-center">
									<tbody>
										<tr class="table-header">
											<td></td>
											<td>Before Deferment</td>
											<td>After Deferment</td>
											<td>Difference</td>
										</tr>
										<tr class="list-row">
											<td class="gray-text text-left">
												Student Loan Balance
											</td>
											<td>${{output.totalPaymentBeforeDeferrment}}</td>
											<td>${{output.totalPaymentAfterDeferrment}}</td>
											<td class="text-primary">${{output.totalDifference}}</td>
										</tr>
										<tr class="list-row">
											<td class="gray-text text-left">
												Monthly Payment
											</td>
											<td>${{output.monthlyPaymentBeforeDeferrment}}</td>
											<td>${{output.monthlyPaymentAfterDeferrment}}</td>
											<td class="text-negative">${{output.monthlyDifference}}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div> -->
						<div class="sfc-col-12 block content">
							<p class="gray-text">
								By deferring a ${{formatNumber(output.balanceBeforeDeferrment)}} loan (or loans) at an
								average interest rate of {{formatNumber(output.avgRate)*100}}% for {{output.totalMonths}} months,
								your <strong>new balance would be ${{formatNumber(output.balanceAfterDeferrment)}}</strong>. This is a
								<strong>difference of ${{formatNumber(output.balanceDifference)}}.</strong>
							</p>
							<p class="gray-text">This calculator assumes that accrued but unpaid interest is capitalized (added to the loan balance) at the end of the deferment period, as opposed to being compounded more frequently.</p>

							<div class="sfc-col-12">
								<script defer src='https://sfc-external-widgets.savingforcollege.com/student_loan_widget_v2/production/loaders/loader.js?loan_type=originator&audience=refinance'></script>
							</div>

							<div class="mt-2">
								<div class="sfc-col-12">
									<h4 class="cardTitle ml-0 pl-0">Next Steps:</h4>
									<ul>
										<li>Find out <a href="/article/how-to-apply-for-an-economic-hardship-deferment" target="_blank">how to apply for an economic hardship deferment</a>.</li>
										<li class="mt-2">Learn <a href="/article/how-to-apply-for-an-unemployment-deferment" target="_blank">how to apply for an unemployment deferment</a>.</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
					<form @submit.prevent="recalculate" class="form sfc-col-12">
						<h4 class="cardTitle">
							How can deferment help you?
							<div class="social-media-area-floated" v-if="!resultDisplayed">
								<sfc-share-component :hero="`https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg`" title="Cost of Deferment Calculator" :url="`${this.$store.state.config.sfcHost}/calculators/cost-of-deferment-calculator`" ></sfc-share-component>
							</div>
						</h4>
						<div class="sfc-row flex content">
							<div class="sfc-col-6">
								<div class="input-group">
									<label for="current-loan" class="label">
										Current Loan Balance</label
									><br />
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
											min="100"
											step="1"
											max="10000000000‬"
											v-model="input.current_loan_balance"
											name="current-loan-balance"
											id="current-loan-balance"
											required
										/>
										<span class="end">$</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>The total amount you owe as of today. An estimate is just fine!</span
											>
										</div>
									</div>
								</div>
								<div class="input-group">
									<label for="current-interest" class="label"
										>Current Interest Rate</label
									><br />
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
											min="0.1"
											step="any"
											max="100"
											v-model="input.current_interest_rate"
											name="current-interest-rate"
											id="current-interest-rate"
											required
										/>
										<span class="end">%</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>The <a href="/article/how-student-loan-interest-works" target="_blank">interest rate</a> you are currently paying, or the average interest rate across the multiple loans you have. Not sure what that is? Try a standard 5% for starters and play what-if games with different rates.</span
											>
										</div>
									</div>
								</div>
							</div>
							<div class="sfc-col-6">
								<div class="input-group">
									<label for="repayment-term" class="label"
										>Repayment Term</label
									><br />
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
											min="1"
											max="50"
											step="1"
											v-model="input.repayment_term"
											name="repayment-term"
											id="repayment-term"
											required
										/>
										<span class="end">YEARS</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>The number of years you have left to pay off what you borrowed. If you started with a 10 year repayment term 3 years ago, this number would be 7.</span
											>
										</div>
									</div>
								</div>
								<div class="input-group">
									<label for="deferment" class="label"
										>Deferment Period</label
									><br />
									<div class="inner">
										<input
											placeholder=""
											class="input postend"
											type="number"
											min="0"
											max="60"
											step="1"
											v-model="input.deferrment_period"
											name="deferrment-period"
											id="deferrment-period"
											required
										/>
										<span class="end">MONTHS</span>
										<div class="tooltip">
											<img
												:src="`${$store.state.config.s3ResourceHost}/images/student-loans/info@2x.svg`"
												alt="info icon vector"
												class="info"
											/>
											<span class="tooltiptext"
												>How long do you plan to pause your payments? Remember, not all lenders allow you to <a href="/article/whats-the-difference-between-a-deferment-and-a-forbearance" target="_blank">defer</a>, and those that do usually require you meet certain conditions. 3-6 months is typical for private loans.</span
											>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div
							class="sfc-row flex"
							style="justify-content: flex-end;"
						>
							<button class="btn btn-primary btn-medium">
								Calculate
							</button>
						</div>
					</form>
				</div>
			</div>
        </div>
		  <!-- Site footer -->
		<footer class="site-footer">
			<div style="margin:0px auto!important;padding:0px 15px;">
				<div class="row">
					<div class="col-sm-10 offset-sm-1 col-lg-6 offset-lg-1 col-xl-5 offset-xl-2 content">
						<h6 class="footer_title">FAQs about cost of deferment calculator</h6>
						<div class="accordion" v-for="index in FAQList" :key="index">
							<div>
								<input type="radio" @click="toggleAccordion(index.id)" name="example_accordion" :id="index.id" class="student-loan-radio accordion__input">
								<label :for="index.id" class="accordion__label">{{ index.question }}</label>
								<div class="accordion__content">
								    <p v-html="index.answer"></p>
								</div>
							</div>
						</div>
					</div>
					<more-calculators></more-calculators>
				</div>
				<div class="row">
					<div class="col-sm-10 offset-sm-1 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2 content">
						<h6 class="footer_title">Related Content</h6>
						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img src="https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20191212072512_stress_facebook.jpeg" alt="Dealing with Student Loans When You’re Unemployed" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/dealing-with-student-loans-when-youre-unemployed" target="_blank">Dealing with Student Loans When You’re Unemployed</a>
								</p>
								<p class="card__detail">
									{{ `Dealing with student loan debt when you are unemployed is extremely stressful. But you do have options, and there is help available if you can’t find a job after graduation. You may be eligible for deferment or forbearance of your loans and calling your lender may lead to lower payments. Let’s take a look at your options if you are unemployed with student loan debt.` | capText}}
								</p>
							</div>
						</article>
						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img src="https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20181224111811_young-couple-bills-worried-kitchen.jpg" alt="What’s the Difference between a Deferment and a Forbearance?" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/whats-the-difference-between-a-deferment-and-a-forbearance" target="_blank">What’s the Difference between a Deferment and a Forbearance?</a>
								</p>
								<p class="card__detail">
									{{ `Private student loans may consider deferments and forbearances to be synonymous, but there are important differences with regard to federal student loans. Both deferments and forbearances provide borrowers with a pause in their student loan payments. The difference between a deferment and a forbearance affects who is responsible for paying the interest on a federal student loan.` | capText}}
								</p>
							</div>
						</article>
						<article class="card_related">
							<div class="card__left">
								<div class="card__image">
									<img src="https://webresources-savingforcollege.s3.us-west-2.amazonaws.com/images/articles/facebook_20190402151814_stock-19.jpeg" alt="Need More Time to Pay Off a Student Loan? Take These Steps" />
								</div>
							</div>
							<div class="card__right">
								<p class="card__info">
									<a href="https://www.savingforcollege.com/article/need-more-time-to-pay-off-a-student-loan-take-these-steps" target="_blank">Need More Time to Pay Off a Student Loan? Take These Steps</a>
								</p>
								<p class="card__detail">
									{{ `Most college graduates and their parents have a six-month grace period before repayment begins on their student loans. For students who graduated in May or June, that means payments are due starting in November or December. But, what if you need more time to pay your student loans? There are options that can further delay your student loan payments.` | capText}}
								</p>
							</div>
						</article>
					</div>
				</div>

			</div>
		</footer>
    </div>
</template>

<script>
import VueScrollTo from 'vue-scrollto'
import { DefermentCalculator } from 'sfc_calcs_js'
import sfcShareComponent from '@/common/components/share-component'
import MoreCalculators from '@/common/components/calculators/more-calculators'

export default {
	layout: 'default_student_loan',
	components: {
		sfcShareComponent,
		MoreCalculators
	},
    data(){
		return {
			input: {
				current_loan_balance: null,
				loanFees: null,
                current_interest_rate: null,
				repayment_term: null,
                deferrment_period: null,
				minMonthlyPayment: 50
            },
            output: {
				totalPaymentBeforeDeferrment: 0.00,
				totalPaymentAfterDeferrment: 0.00,
				monthlyPaymentBeforeDeferrment: 0.00,
				monthlyPaymentAfterDeferrment: 0.00,
				totalDifference:0.00,
				monthlyDifference:0.00,
				balanceBeforeDeferrment:0.00,
				balanceAfterDeferrment:0.00,
				avgRate:0.00,
				totalMonths:0,
				balanceDifference:0.00
			},
			resultDisplayed:false,
			FAQList:[
                {id: 1,
                question: "What does it mean to put your student loans into deferment?",
                answer: "Deferment means that you temporarily pause payments on your student loans if you are struggling to make payments."},
                {id: 2,
                question: "What circumstances allow for a deferment?",
                answer: "You can put your federal student loans in deferment if you are in-school, unemployed, undergoing an economic hardship (or in the Peace Corps), under active cancer treatment and serving on active duty in the U.S. Armed Forces. Options for deferments (forbearances) for private lenders vary by lender."},
                {id: 3,
                question: "Am I eligible to defer my student loans?",
                answer: "Each type of deferment has different qualification and application requirements. Contact your lender to see what is available."},
                {id: 4,
                question: "How long can I defer student loans for?",
                answer: "How long you can defer student loans depends on many variables – it depends if your loans are federal or private, what type of federal loan it is, when you borrowed, what type of deferment you are applying for, and if private, what your lender is offering. The deferment may have a time limit."},
                {id: 5,
                question: "What’s the difference between deferment and forbearance?",
                answer: "Interest accrues on all student loans in forbearance, but not on subsidized federal student loans in a deferment. A forbearance may be easier to qualify for."},
                {id: 6,
                question: "What are the pros and cons of putting a student loan in deferment?",
                answer: "The main advantage of deferment is that it temporarily relieves you from making payments, which could help if you lose your job or are struggling financially. This could make paying other bills easier. Plus, it means you won’t be late on payments or in default, which could hurt your credit. The disadvantages of deferment is that you’re not paying down your student loan debt. Unless you have federal subsidized loans, interest will continue to accrue during your deferment. If the interest isn’t paid as it accrues, it will be added to the loan balance, increasing the size of the debt."},
                {id: 7,
                question: "I applied for a deferment. What should I do now?",
                answer: "Follow the instructions given to you by your lender. You should continue to make payments until you have been officially approved for a deferment and have a confirmation."},
                {id: 8,
                question: "Can I put my private student loans in deferment?",
                answer: "Some private lenders do offer a chance for you to put your loans into a forbearance. Some lenders will use the terms deferment and forbearance as synonyms Contact your lender directly to find out what options they offer and how to apply."},
				{id: 9,
                question: "Does putting my student loans in deferment impact my credit?",
                answer: "Once you put your student loans in deferment, it will be noted on your credit report. But it does not negatively impact your credit score. Missing payments and defaulting on your student loans will negatively impact your credit much more."},
            	{id: 10,
                question: "If I can’t defer my student loans, are there other options?",
                answer: "If you have used up your allotted deferment time for federal student loans, you can consider forbearances, Income-Driven Repayment Plans and Extended Repayment Plans. These can reduce your monthly payment amount. For private loans that don’t offer deferment options, ask about temporarily lowering payments."}
			]
		}
	},
	mounted() {
		document.body.className = 'gray';
		setTimeout(() => {
			document.getElementById("sfc-main-content").classList.add("student-loan-calculator-content");
		}, 1000)
	},
	methods: {
		recalculate($event){


			let currentLoan = new DefermentCalculator({
				loanBalance: this.input.current_loan_balance,
				annualInterestRate: this.input.current_interest_rate/100.0,
				repaymentTermInYears: this.input.repayment_term,
				deferrmentPeriod: this.input.deferrment_period,
				fees: this.input.loanFees==null?0:this.input.loanFees/100.0,
				minimumPayment: this.input.minMonthlyPayment
            });
			this.result = currentLoan.calculate().output;

			this.output.totalPaymentBeforeDeferrment = this.result.beforeDeferrment.totalPayments.toFixed(2);
			this.output.totalPaymentAfterDeferrment = this.result.afterDeferrment.totalPayments.toFixed(2);
			this.output.monthlyPaymentBeforeDeferrment = this.result.beforeDeferrment.monthlyPayment.toFixed(2);
			this.output.monthlyPaymentAfterDeferrment = this.result.afterDeferrment.monthlyPayment.toFixed(2);
			this.output.totalDifference = (this.result.afterDeferrment.totalPayments-this.result.beforeDeferrment.totalPayments).toFixed(2);
			this.output.monthlyDifference = (this.result.afterDeferrment.monthlyPayment-this.result.beforeDeferrment.monthlyPayment).toFixed(2);
			this.output.avgRate = this.result.avgRate;
			this.output.totalMonths = this.result.totalMonths;
			this.output.balanceBeforeDeferrment = this.result.balanceBeforeDeferrment;
			this.output.balanceAfterDeferrment = this.result.balanceAfterDeferrment.toFixed(2);
			this.output.balanceDifference = this.result.balanceDifference.toFixed(2);

			this.resultDisplayed = true;
			setTimeout(() => {
				VueScrollTo.scrollTo('#res');
			}, 200)
			$event.preventDefault();
		},
		formatNumber(number)
		{
			number = Number(number).toLocaleString();

			// return number;

			let string = number.split('.');
			if(string[1]!=undefined)
			{
				string = string[1];

				if(string.length==1)
					return number+"0";
				else
					return number;
			}
			else
			{
				return number+".00";
			}

		},
		toggleAccordion(section){
			if (this.lastSectionOpened == section) {
				let input = document.getElementById(section)
				if (input.checked) {
					input.checked = false
				}
				this.lastSectionOpened = ""
			} else {
				this.lastSectionOpened = section
			}
		},
        create_schema(FAQList) {
            let jsonld = '{ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [';
            for (var i = 0; i < FAQList.length; i++) {
                if((i + 1) == FAQList.length){
                    jsonld += '{ "@type": "Question", "name": "' + this.FAQList[i].question +'" , "acceptedAnswer": { "@type": "Answer", "text": "' + this.FAQList[i].answer + '" }}';
                }else{
                    jsonld += '{ "@type": "Question", "name": "' + this.FAQList[i].question +'" , "acceptedAnswer": { "@type": "Answer", "text": "' + this.FAQList[i].answer + '"}},';
                }
            }
            jsonld += ']}';
            return jsonld;
        }
	},
	head(){
		return {
			title: "Cost of Deferment Calculator",
			meta: [
				{hid: "ogmetatitle", property: 'og:title', content: "Cost of Deferment Calculator"},
				{hid: "metadescription", name: 'description', content: "This cost of deferment calculator analyzes the cost of suspending payments on your student loans for a period of time using a deferment or forbearance."},
				{hid: "ogmetadescription", property: 'og:description', content: "This cost of deferment calculator analyzes the cost of suspending payments on your student loans for a period of time using a deferment or forbearance."},
				{
					hid: 'og-url',
					name: 'og:url',
					property: 'og:url',
					content: `${this.$store.state.config.sfcHost}/calculators/cost-of-deferment-calculator`
				},
				{
					hid: 'og-image',
					name: 'og:image',
					property: 'og:image',
					content: 'https://web-resources.savingforcollege.com/images/cost-of-deferment-calculator-thumbnail.jpg'
				},
			],
			script: [
				{ type: 'text/javascript', src: 'https://s.ntv.io/serve/load.js', async: true, defer: true}
			],
			__dangerouslyDisableSanitizers: ['title', 'meta'],
		}
	},
	filters: {
		capText(value){
			if(value.length < 165) {
				return value
			}
			let paragraph = ""
			let i = 0
			const words = value.split(" ")
			while (paragraph.length < 165 && (paragraph.length + words[i].length < 165)) {
				paragraph += ` ${words[i]}`
				i++
			}
			return paragraph.trim() + "..."
		}
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    /* I have included page specific styles to the card here, widths and classes specifically for this calculator the rest of the main styles can be found in the "styles.css" */
	.card {
		width: 70%;
		margin-top: 40px;
		margin-left: 15%;
	}
	.card .form {
		width: 100%;
		margin: 0px 0%;
	}
	.form .btn {
		margin-top: 28px;
	}
	.completed {
		width: 100%;
		margin-left: 0%;
		display: flex;
		padding: 20px 0px;
		border-bottom: 1px solid #dbe2e7;
	}

	.results {
		width: 94%;
		padding: 20px 0px;
		margin-left: 0px;
		box-sizing: border-box;
	}
	.graybg {
		border-radius: 0px 8px 8px 0px;
	}
	tbody{
		/* width: auto !important; */
		display: inline-table;
	}
	tbody td.large {
		font-size: 18px !important
	}

	tbody td.larger {
		font-size: 24px !important
	}

	.text-navy{
		color: rgb(18, 83, 147) !important;
	}

	@media only screen and (min-width: 1200px) {
		.tooltip {
			margin-left: 10px;
		}
	}

	@media only screen and (max-width: 1220px) {
		.card {
			width: 90%;
			margin-top: 40px;
			margin-left: 4%;
		}
		.card.completed {
			width: 100%;
			margin-left: 0%;
		}
	}

	@media only screen and (max-width: 768px) {
		.card {
			width: 100%;
			margin-top: 40px;
			margin-left: 0%;
			display: inline-block;
		}
		.form .flex {
			display: inline-block !important;
		}
		.card.completed .form {
			width: 90%;
		}
		.card .form {
			width: 100%;
		}
		.flex.checkbox-group {
			display: flex !important;
			width: 80%;
		}
		.bluebg {
			background: #125393;
			color: #fff;
			border-radius: 0px;
		}
	}
</style>
